import type { PostHog } from 'posthog-js';
import { readConsent, writeConsent } from './consent';

// PostHog web analytics (EU Cloud) — pageviews with scroll depth, clicks, a click
// heatmap, and which landing sections a visitor reaches — strictly opt-in. Until the visitor accepts in the cookie banner the SDK is not even
// downloaded: no cookies, no storage, no requests (§ 25 TDDDG, Art. 6 (1) (a) GDPR).
// Rejecting sends nothing; withdrawing later stops capture and deletes what PostHog
// stored.
//
// Disabled unless VITE_POSTHOG_KEY is baked in at build time, so local dev never
// reports into the production project. The SDK is fetched as its own async chunk,
// so it never weighs on the landing page's initial bundle.

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com';

/** Whether there is anything to ask consent for — no key, no banner. */
export const analyticsAvailable = Boolean(POSTHOG_KEY);

let loading: Promise<PostHog | null> | null = null;

function load(): Promise<PostHog | null> {
  if (!POSTHOG_KEY) return Promise.resolve(null);
  const key = POSTHOG_KEY;
  loading ??= import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(key, {
        api_host: POSTHOG_HOST,
        // SPA pageviews on history changes, and URL fragments stripped from captured
        // URLs — which keeps Keycloak's `#state=…&code=…` redirect out of the events.
        defaults: '2026-08-30',
        person_profiles: 'identified_only',
        disable_session_recording: true,
        // Host-only cookie: nothing lands on auth.* or admin.*, and withdrawal can
        // delete it without guessing the domain.
        cross_subdomain_cookie: false,
        // Where people click (the heatmap) and clicks on things that don't respond,
        // set here so they don't depend on toggles in the PostHog project.
        capture_heatmaps: true,
        capture_dead_clicks: true,
        // Dev builds print every event to the console, so the landing's events can be
        // checked against a throwaway key without opening the PostHog project.
        before_send: import.meta.env.DEV
          ? (event) => {
              if (event) console.debug('[posthog]', event.event, JSON.stringify(event.properties));
              return event;
            }
          : undefined,
      });
      return posthog;
    })
    // A blocked or failed load (ad blockers do this) just means no analytics.
    .catch(() => {
      loading = null;
      return null;
    });
  return loading;
}

/** The home page sections worth a funnel step, top to bottom. */
export type LandingSection =
  | 'before'
  | 'how'
  | 'week'
  | 'protein'
  | 'recipes'
  | 'cook'
  | 'pricing'
  | 'faq'
  | 'close';

/**
 * The landing's own events, each with a fixed set of properties, so a typo can't
 * silently start a new event in PostHog. Every event also carries `lang`, the language
 * the page was shown in.
 */
export type LandingEvent =
  | { name: 'landing_section_viewed'; props: { section: LandingSection } }
  | { name: 'landing_scroll_depth'; props: { percent: 25 | 50 | 75 | 100 } }
  | {
      name: 'landing_cta_clicked';
      props: { location: 'nav' | 'hero' | 'close'; channel: 'appStore' | 'testflight' | 'invite' };
    }
  | { name: 'landing_nav_clicked'; props: { target: string; area: 'header' | 'footer' } }
  | { name: 'landing_language_changed'; props: { from: string; to: string } }
  | { name: 'landing_theme_changed'; props: { to: 'light' | 'dark' } }
  | { name: 'landing_faq_opened'; props: { question: number } }
  | { name: 'landing_carousel_stepped'; props: { direction: 'next' | 'back' } }
  | { name: 'landing_hero_stepped'; props: { step: number; how: 'click' | 'tap' | 'swipe' } }
  | {
      name: 'landing_cook_demo_stepped';
      props: { direction: 'next' | 'back' | 'restart'; step: number };
    }
  | {
      name: 'landing_contact_clicked';
      props: { channel: 'support_email' | 'hello_email' | 'support_page' };
    }
  | {
      name: 'landing_legal_clicked';
      props: { page: 'impressum' | 'privacy' | 'terms' | 'delete_account' | 'cookie_settings' };
    }
  | { name: 'analytics_consent_granted'; props: Record<string, never> };

/**
 * Sends one event — only once analytics are consented to. Nothing is queued before
 * the choice, so what happened before it stays unrecorded.
 */
export function track<N extends LandingEvent['name']>(
  name: N,
  props: Extract<LandingEvent, { name: N }>['props'],
): void {
  if (!loading) return;
  const lang = document.documentElement.lang || undefined;
  void loading.then((posthog) => posthog?.capture(name, { ...props, lang }));
}

/** Whether events are being sent right now: consent given and the SDK loading or loaded. */
export const analyticsOn = (): boolean => loading !== null;

/** A visitor reached a landing section. */
export function trackSectionViewed(section: LandingSection): void {
  track('landing_section_viewed', { section });
}

/** On startup: load PostHog only for a visitor who has already accepted. */
export function initAnalytics(): void {
  if (readConsent() === 'granted') void load();
}

export function grantAnalyticsConsent(): void {
  writeConsent('granted');
  void load().then((posthog) => {
    // Only after a withdrawal on this same page; a fresh instance is already capturing.
    if (posthog?.has_opted_out_capturing()) posthog.opt_in_capturing();
    // Sent after the yes, never for a no: a rejection must leave no trace.
    track('analytics_consent_granted', {});
  });
}

export function denyAnalyticsConsent(): void {
  writeConsent('denied');
  // Never loaded on this page: nothing was stored, nothing to undo.
  if (!loading) return;
  void loading.then((posthog) => {
    posthog?.opt_out_capturing();
    clearPostHogStorage();
    // A reload leaves no live SDK behind that could write its state back; with
    // consent denied, the next load never fetches PostHog at all.
    window.location.reload();
  });
}

/** PostHog keeps its state in `ph_*` / `__ph_*` cookies and web-storage entries. */
function clearPostHogStorage(): void {
  const ours = (name: string) => name.startsWith('ph_') || name.startsWith('__ph_');
  // Getters, not the objects: merely touching `localStorage` throws where storage is blocked.
  for (const storage of [() => localStorage, () => sessionStorage]) {
    try {
      const store = storage();
      Object.keys(store)
        .filter(ours)
        .forEach((k) => store.removeItem(k));
    } catch {
      // Storage unavailable: nothing could have been written there either.
    }
  }
  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
  document.cookie
    .split(';')
    .map((c) => c.split('=')[0].trim())
    .filter(ours)
    .forEach((name) => {
      document.cookie = `${name}=; ${expired}`;
      document.cookie = `${name}=; ${expired}; domain=.${window.location.hostname}`;
    });
}
